import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import StickyCallButton from './components/StickyCallButton';
import WhatsAppChatWidget from './components/WhatsAppChatWidget';
import LeadCapturePopup from './components/LeadCapturePopup';
import ProposalModal from './components/ProposalModal';
import { MobileActionBar } from './components/MobileActionBar';
import { Toaster } from './components/ui/sonner';

// Lazy load non-critical pages for better initial load performance
const DestinationWeddings = lazy(() => import('./pages/DestinationWeddings'));
const WeddingDecor = lazy(() => import('./pages/WeddingDecor'));
const CompleteCelebration = lazy(() => import('./pages/CompleteCelebration'));
const SoundEntertainment = lazy(() => import('./pages/SoundEntertainment'));
const Events = lazy(() => import('./pages/Events'));
const Honeymoon = lazy(() => import('./pages/Honeymoon'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const FAQs = lazy(() => import('./pages/FAQs'));
const Packages = lazy(() => import('./pages/Packages'));
const DestinationTemplate = lazy(() => import('./pages/destinations/DestinationTemplate'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const CancellationPolicy = lazy(() => import('./pages/legal/CancellationPolicy'));
const EventsWeddings = lazy(() => import('./pages/EventsWeddings'));
const WeddingProposal = lazy(() => import('./pages/WeddingProposal'));
const CorporateEvents = lazy(() => import('./pages/CorporateEvents'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RSVPPage = lazy(() => import('./pages/RSVPPage'));
const LogisticsView = lazy(() => import('./pages/LogisticsView'));
const VendorRegistration = lazy(() => import('./pages/VendorRegistration'));
const LocationLandingPage = lazy(() => import('./pages/locations/LocationLandingPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#666] text-sm">Loading...</p>
    </div>
  </div>
);

// Layout wrapper that conditionally renders Header/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isRsvpRoute = location.pathname.startsWith('/rsvp');
  const isLogisticsRoute = location.pathname.startsWith('/logistics');
  const isVendorRoute = location.pathname.startsWith('/vendors');
  const isBare = isAdminRoute || isRsvpRoute || isLogisticsRoute;
  
  return (
    <>
      {!isBare && <Header />}
      <main id="main-content" role="main">
        {children}
      </main>
      {!isBare && <Footer />}
      {!isBare && (
        <div className={isVendorRoute ? '' : 'hidden sm:block'}>
          <WhatsAppChatWidget />
          <StickyCallButton />
        </div>
      )}
      {!isBare && !isVendorRoute && <MobileActionBar />}
      {!isBare && !isVendorRoute && <div className="sm:hidden" style={{ height: 'calc(76px + env(safe-area-inset-bottom))' }} aria-hidden="true" />}
      {!isBare && !isVendorRoute && <LeadCapturePopup />}
      {!isBare && !isVendorRoute && <ProposalModal />}
      <Toaster position="top-center" richColors />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/destination-weddings" element={<DestinationWeddings />} />
                <Route path="/wedding-decor" element={<WeddingDecor />} />
                <Route path="/complete-celebration" element={<CompleteCelebration />} />
                <Route path="/sound-entertainment" element={<SoundEntertainment />} />
                <Route path="/events" element={<Events />} />
                <Route path="/honeymoon" element={<Honeymoon />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:id" element={<BlogPost />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/faqs" element={<FAQs />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/destinations/:destination" element={<DestinationTemplate />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/events-weddings" element={<EventsWeddings />} />
                <Route path="/wedding-proposal" element={<WeddingProposal />} />
                <Route path="/corporate-events" element={<CorporateEvents />} />
                {/* Vendor registration */}
                <Route path="/vendors/register" element={<VendorRegistration />} />
                {/* Location Landing Pages */}
                <Route path="/weddings/:location" element={<LocationLandingPage />} />
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                {/* Short, mobile-friendly aliases for the admin dashboard */}
                <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/login" element={<Navigate to="/admin/login" replace />} />
                {/* Public RSVP page */}
                <Route path="/rsvp/:slug" element={<RSVPPage />} />
                {/* Read-only shared logistics list */}
                <Route path="/logistics/:token" element={<LogisticsView />} />
                {/* 404 catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;